import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Box, Button, Card, CardContent, Divider, FormControlLabel, Grid, IconButton, Slider,
    Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AngleProfileDialog from "./AngleProfileDialog";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { cardTitleSx, numberSx, tokens } from "../../theme";
import { roundToX } from "../../common/common";
import {
    applyThresholds, applyRawSurfaceFilters, clearShaftRange, setShaftRange,
    setCircleFixLimit, setCircleFixEnabled, setCircleFixMode, setAdaptiveK,
    setAngleLimit, setAngleEnabled, setPointLimit, setPointEnabled, setMaxSigma, setSigmaEnabled,
    setShowRejected, setDistanceToFF, setDistanceFtoR,
    setNippleClipEnabled, setEdgeClipEnabled,
    PRODUCTION_CIRCLE_FIX_LIMIT, PRODUCTION_ANGLE_LIMIT, PRODUCTION_POINT_LIMIT, DEFAULT_MAX_SIGMA,
    CircleFixMode,
} from "../../reducer/RawSurfaceSlice";
import { rangesOverlap, readRange, computeMotorRight, getFilterRejectReasons } from "./shaftRanges";
import RawSurfaceCorrectionCard from "./RawSurfaceCorrectionCard";
import NoiseReductionScatterCard from "./NoiseReductionScatterCard";
import FocusRemoveWorstCard from "./FocusRemoveWorstCard";
import "../cards/ShimPanel.css";

/** Small info icon + tooltip, replacing a permanently-visible caption line under each threshold. */
const InfoTooltip = ({ text }) => (
    <Tooltip title={text} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 15, color: tokens.muted, cursor: "help" }} />
    </Tooltip>
);

/**
 * Right-rail controls for the Raw Surface Analysis tab.
 *
 * Circle fix runs first (it prunes points within a circle and re-fits), then angle/point/sigma
 * gate whole circles against the (possibly fixed) result - the server enforces that order
 * regardless of what's toggled, but the controls are ordered top-to-bottom the same way so the
 * UI reads as the actual pipeline.
 *
 * Sliders update local state on drag and only dispatch on commit (onChangeCommitted), so a
 * drag costs one request instead of one per pixel.
 */
export default function RawSurfaceSettings() {
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();

    const fitting = rawSurface.status === "fitting";
    const isBusy = fitting || rawSurface.status === "uploading";

    const filterRejectedX = useMemo(
        () => new Set(getFilterRejectReasons(rawSurface.filterTrace).keys()),
        [rawSurface.filterTrace]
    );

    // Center Points Summary counts every reason a point doesn't count toward the shim - circle-fix/
    // angle/point/sigma gating (circles[].kept) and NoiseReductionScatter/FocusRemoveWorst
    // (filterRejectedX) both count as rejected, not just the former (Kevin, 2026-08-29: "update
    // the values based on the total number of remaining points, not just the ones based on
    // circle fixing"). rawSurface.kept/dropped (the server's own /fit response counts) only ever
    // reflected circle-fix/gating, which is why this is computed client-side instead.
    const pointsKept = useMemo(
        () => rawSurface.circles.filter((c) => c.kept && c.pointCount > 0 && !filterRejectedX.has(c.x)).length,
        [rawSurface.circles, filterRejectedX]
    );
    const pointsRejected = useMemo(
        () => rawSurface.circles.filter((c) => c.pointCount > 0 && (!c.kept || filterRejectedX.has(c.x))).length,
        [rawSurface.circles, filterRejectedX]
    );
    const atProductionDefaults =
        rawSurface.circleFixEnabled && rawSurface.circleFixMode === CircleFixMode.Fixed &&
        rawSurface.circleFixLimit === PRODUCTION_CIRCLE_FIX_LIMIT &&
        rawSurface.angleEnabled && rawSurface.angleLimit === PRODUCTION_ANGLE_LIMIT &&
        rawSurface.pointEnabled && rawSurface.pointLimit === PRODUCTION_POINT_LIMIT &&
        !rawSurface.sigmaEnabled;

    const commit = (overrides) => dispatch(applyThresholds(overrides));

    // Gates the Surface Correction / Data Filter / Shim panels below - shim is meaningless
    // without both shaft ranges and both foot positions, so these panels stay hidden (with an
    // explanation in their place) until all four are set.
    const rangesAndFeetSet = Boolean(
        rawSurface.machineRange && rawSurface.motorRange
        && rawSurface.distanceToFF != null && rawSurface.distanceFtoR != null
    );
    const commitFilters = () => dispatch(applyRawSurfaceFilters());

    // Inferred from where the two shaft ranges actually sit, rather than an operator-entered
    // flag the way Scan Analysis's motorRight is - only meaningful once both are set, and shared
    // with RawSurfaceSaveDialog.js's save payload so the label here and what's actually sent to
    // the server can never disagree.
    const motorRight = computeMotorRight(rawSurface.machineRange, rawSurface.motorRange);

    // Typed entry for the Shaft Ranges card, alongside the existing drag-to-select-on-chart path.
    // Local text state so a field can hold a half-typed or momentarily-invalid value without
    // touching Redux; committed on blur (see commitShaftRange) rather than per keystroke, matching
    // this card's existing sliders (local value on drag, dispatch on settle). Re-synced from Redux
    // whenever the range actually changes - covers a drag-select updating it externally - which is
    // safe against clobbering active typing because Redux only changes here on a *completed*
    // action (a finished drag+popover-confirm, or this card's own blur-commit), never mid-keystroke.
    const [machineStartText, setMachineStartText] = useState("");
    const [machineStopText, setMachineStopText] = useState("");
    const [motorStartText, setMotorStartText] = useState("");
    const [motorStopText, setMotorStopText] = useState("");
    const [rangeError, setRangeError] = useState(null);
    const [distanceToFFText, setDistanceToFFText] = useState("");
    const [distanceFtoRText, setDistanceFtoRText] = useState("");
    const [footError, setFootError] = useState(null);

    useEffect(() => {
        setMachineStartText(rawSurface.machineRange ? Math.round(rawSurface.machineRange.start) : "");
        setMachineStopText(rawSurface.machineRange ? Math.round(rawSurface.machineRange.stop) : "");
    }, [rawSurface.machineRange]);

    useEffect(() => {
        setMotorStartText(rawSurface.motorRange ? Math.round(rawSurface.motorRange.start) : "");
        setMotorStopText(rawSurface.motorRange ? Math.round(rawSurface.motorRange.stop) : "");
    }, [rawSurface.motorRange]);

    useEffect(() => {
        setDistanceToFFText(rawSurface.distanceToFF != null ? rawSurface.distanceToFF : "");
    }, [rawSurface.distanceToFF]);

    useEffect(() => {
        setDistanceFtoRText(rawSurface.distanceFtoR != null ? rawSurface.distanceFtoR : "");
    }, [rawSurface.distanceFtoR]);

    /**
     * Validates and commits one shaft's typed start/stop on blur - same rules RawSurfaceSaveDialog
     * and the drag popover already enforce (start < stop, no overlap with the other shaft), reused
     * from shaftRanges.js rather than re-implemented a third time. A partial entry (one field
     * filled) is left alone rather than guessed at; a validation failure leaves the typed text as
     * -is (not force-reverted) so it stays visible to fix, with the reason shown below the fields.
     */
    const commitShaftRange = (shaft, startText, stopText, otherRange) => {
        const entry = readRange(startText, stopText);
        const label = shaft === "machine" ? "Machine" : "Motor";

        if (entry.state === "partial") return;

        if (entry.state === "cleared") {
            setRangeError(null);
            dispatch(clearShaftRange({ shaft }));
            return;
        }

        // Text fields (switched from type="number" so maxLength can actually cap length - see
        // the fields below) no longer block non-digit characters at the keystroke level, so a
        // bad value can reach here as NaN rather than never being typeable at all.
        if (!Number.isFinite(entry.range.start) || !Number.isFinite(entry.range.stop)) {
            setRangeError(`${label} start/stop must be numbers.`);
            return;
        }
        if (entry.range.start >= entry.range.stop) {
            setRangeError(`${label} start must be less than ${label} stop.`);
            return;
        }
        if (rangesOverlap(entry.range, otherRange)) {
            setRangeError("Machine and Motor ranges overlap — choose ranges that don't overlap.");
            return;
        }

        setRangeError(null);
        dispatch(setShaftRange({ shaft, start: entry.range.start, stop: entry.range.stop }));
    };

    /**
     * Validates and commits one foot-position field on blur - same "> 0" requirement as the
     * numbers themselves (a zero or negative foot position is not physically meaningful), no
     * range-overlap logic needed since these are single values, not spans. `dispatchSetter` is
     * one of setDistanceToFF/setDistanceFtoR, already bound to its own payload key.
     */
    const commitFootPosition = (text, dispatchSetter) => {
        if (text === "") {
            setFootError(null);
            dispatchSetter(null);
            return;
        }
        const value = Number(text);
        if (!Number.isFinite(value) || value <= 0) {
            setFootError("Foot positions must be a positive number.");
            return;
        }
        setFootError(null);
        dispatchSetter(value);
    };

    // Same conversion/rounding/grid RawSurfaceSaveDialog.js and ShimsCard.js (Scan Analysis) use,
    // so a shim value reads identically wherever it's shown in the app.
    const MICRON_TO_INCH = 0.00003937;
    const shimInches = (microns) =>
        typeof microns === "number" ? roundToX(microns * MICRON_TO_INCH, 3).toFixed(3) : "-";

    // Same Std Dev/R2 stats StatsCard.js shows for Scan Analysis (state.laserData.summary), just
    // reading rawSurface.summary instead - scan.Result.summary's per-shaft best-fit-line stats,
    // now returned by scan/parse alongside shims/filterTrace. Same 20%-per-column widths as that
    // card (label + 2 shafts x 2 stats = 5 columns), which is what actually keeps the row from
    // overflowing its container and wrapping - a previous version of this summed to 125%.
    const statsLabelSx = { display: "inline-flex", width: "20%", fontSize: "12px", whiteSpace: "nowrap" };
    const statsGroupSx = { display: "inline-flex", width: "40%", justifyContent: "center", fontSize: "12px" };
    const statsCellSx = { display: "inline-flex", width: "20%", justifyContent: "right", fontSize: "12px" };
    const renderStatsHeader = () => (
        <>
            <Grid container direction="row" wrap="nowrap">
                <Box sx={statsLabelSx} />
                <Box sx={statsGroupSx}>Machine</Box>
                <Box sx={statsGroupSx}>Motor</Box>
            </Grid>
            <Grid container direction="row" wrap="nowrap">
                <Box sx={statsLabelSx} />
                <Box sx={statsCellSx}>Std Dev</Box>
                <Box sx={statsCellSx}>R2</Box>
                <Box sx={statsCellSx}>Std Dev</Box>
                <Box sx={statsCellSx}>R2</Box>
            </Grid>
        </>
    );
    const renderStatsRow = (machine, motor, dir, label) => (
        <Grid container direction="row" wrap="nowrap">
            <Box sx={statsLabelSx}>{label}</Box>
            <Box sx={statsCellSx}>{roundToX(machine.standardDeviation[dir], 4).toFixed(4)}</Box>
            <Box sx={statsCellSx}>{roundToX(machine.rSquared[dir], 4).toFixed(4)}</Box>
            <Box sx={statsCellSx}>{roundToX(motor.standardDeviation[dir], 4).toFixed(4)}</Box>
            <Box sx={statsCellSx}>{roundToX(motor.rSquared[dir], 4).toFixed(4)}</Box>
        </Grid>
    );

    return (
        <Stack spacing={2}>
            {/* First thing in the rail: it is the one analysis that must be run deliberately,
                and it used to be the hardest thing on the page to find. Opens in a modal - see
                AngleProfileDialog for why it is not inline. */}
            <AngleProfileDialog />
            {/* Center Points summary first, thresholds below - matches the mockup's rail order. */}
            <Card>
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing="7px">
                        <Typography sx={cardTitleSx}>Center Points Summary</Typography>
                        {/* Fades in alongside the chart-wide "Updating fit..." overlay - this is
                            the same signal, just also visible when the charts themselves are
                            scrolled out of view. */}
                        {fitting && (
                            <>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: tokens.warn }} />
                                <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: tokens.warn }}>
                                    Updating&hellip;
                                </Typography>
                            </>
                        )}
                    </Stack>

                    {/* Stat tiles per the mockup: inset surface-2 panel, small uppercase label,
                        large tabular-figure value tinted with the same kept/reject colours the
                        chart uses, so the numbers and the plot read as one system. */}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        {[
                            { label: "Kept", value: pointsKept, color: tokens.kept },
                            { label: "Rejected", value: pointsRejected, color: tokens.reject },
                        ].map((stat) => (
                            <Box
                                key={stat.label}
                                sx={{
                                    flex: 1,
                                    borderRadius: "8px",
                                    padding: "8px 10px",
                                    backgroundColor: tokens.surface2,
                                }}
                            >
                                <Typography sx={{
                                    fontSize: "10px",
                                    color: tokens.muted,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                }}>
                                    {stat.label}
                                </Typography>
                                <Typography sx={{
                                    ...numberSx,
                                    fontSize: "18px",
                                    fontWeight: 700,
                                    color: stat.color,
                                }}>
                                    {stat.value.toLocaleString()}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <FormControlLabel
                        sx={{ mt: 0.5, ml: 0 }}
                        control={
                            <Switch
                                size="small"
                                checked={rawSurface.showRejected}
                                onChange={(e) => dispatch(setShowRejected({ show: e.target.checked }))}
                            />
                        }
                        label="Show rejected points"
                        slotProps={{ typography: { variant: "caption", color: "text.secondary" } }}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography sx={cardTitleSx}>Circle Fit Thresholds</Typography>

                    <Typography variant="caption" color="text.secondary">
                        Circle fix prunes points and re-fits; the thresholds below then filter
                        already-fitted circles. Defaults match the AU's own settings.
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Switch
                                    size="small"
                                    checked={rawSurface.circleFixEnabled}
                                    disabled={isBusy}
                                    onChange={(e) => {
                                        dispatch(setCircleFixEnabled({ enabled: e.target.checked }));
                                        commit({ circleFixEnabled: e.target.checked });
                                    }}
                                />
                                <Typography variant="body2">Circle fix</Typography>
                                <InfoTooltip text="Fixed: drops points farther than a set distance from the circle's fitted edge, then re-fits once. Adaptive (CF1): iteratively drops points beyond k times the fit's own residual scatter, refitting each pass. Runs before the thresholds below either way - fixing a circle first can save it from being dropped by them." />
                            </Stack>
                        </Stack>

                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            fullWidth
                            value={rawSurface.circleFixMode}
                            disabled={isBusy || !rawSurface.circleFixEnabled}
                            onChange={(e, v) => {
                                if (v === null) return; // exclusive group - ignore re-clicking the active one
                                dispatch(setCircleFixMode({ circleFixMode: v }));
                                commit({ circleFixMode: v });
                            }}
                            sx={{ mt: 0.5, mb: 1 }}
                        >
                            <ToggleButton value={CircleFixMode.Fixed} sx={{ fontSize: "11.5px", py: "3px" }}>
                                Fixed distance
                            </ToggleButton>
                            <ToggleButton value={CircleFixMode.Adaptive} sx={{ fontSize: "11.5px", py: "3px" }}>
                                Adaptive (CF1)
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {rawSurface.circleFixMode === CircleFixMode.Adaptive ? (
                            <>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2">k (multiplier)</Typography>
                                    <Typography variant="body2" sx={numberSx}>{rawSurface.adaptiveK.toFixed(1)}</Typography>
                                </Stack>
                                <Slider
                                    size="small"
                                    min={2.0}
                                    max={4.0}
                                    step={0.1}
                                    value={rawSurface.adaptiveK}
                                    disabled={isBusy || !rawSurface.circleFixEnabled}
                                    onChange={(e, v) => dispatch(setAdaptiveK({ adaptiveK: v }))}
                                    onChangeCommitted={(e, v) => commit({ adaptiveK: v })}
                                />
                            </>
                        ) : (
                            <>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2">Distance</Typography>
                                    <Typography variant="body2" sx={numberSx}>{rawSurface.circleFixLimit}&nbsp;µm</Typography>
                                </Stack>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={1000}
                                    step={10}
                                    value={rawSurface.circleFixLimit}
                                    disabled={isBusy || !rawSurface.circleFixEnabled}
                                    onChange={(e, v) => dispatch(setCircleFixLimit({ circleFixLimit: v }))}
                                    onChangeCommitted={(e, v) => commit({ circleFixLimit: v })}
                                />
                            </>
                        )}
                    </Box>

                    {/* Angular clipping sits between circle fixing and the circle-level
                        thresholds, matching the order it actually runs in: it cuts points, fixing
                        then cuts more, and only then do the thresholds judge whole circles. */}
                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Switch
                                size="small"
                                checked={rawSurface.nippleClipEnabled}
                                disabled={isBusy}
                                onChange={(e) => {
                                    dispatch(setNippleClipEnabled({ enabled: e.target.checked }));
                                    commit({ nippleClipEnabled: e.target.checked });
                                }}
                            />
                            <Typography variant="body2">Clip nipple</Typography>
                            <InfoTooltip text="Drops points inside the specular bump at the top of each circle, using the window measured from this capture's own Residual vs Angle profile - not a fixed angle. Runs before circle fixing, so fixing's residuals are measured against a fit the bump hasn't already bent. Measured per shaft range when ranges are set. If no bump clears the detection threshold, nothing is dropped." />
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                            <Switch
                                size="small"
                                checked={rawSurface.edgeClipEnabled}
                                disabled={isBusy}
                                onChange={(e) => {
                                    dispatch(setEdgeClipEnabled({ enabled: e.target.checked }));
                                    commit({ edgeClipEnabled: e.target.checked });
                                }}
                            />
                            <Typography variant="body2">Clip edges</Typography>
                            <InfoTooltip text="Drops points beyond the usable arc measured for this capture, replacing the fixed ±45° assumption. The usable arc depends on shaft diameter: a large shaft runs out of the laser's field of view, a small one degrades from grazing incidence first. The minimum-central-angle threshold below still judges the arc as acquired, not as clipped, so this can't cause circles to be rejected for a narrowing it just performed." />
                        </Stack>

                        {rawSurface.edgeClipEnabled && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                Experimental — review the measured arc on the Residual vs Angle card first.
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Switch
                                    size="small"
                                    checked={rawSurface.angleEnabled}
                                    disabled={isBusy}
                                    onChange={(e) => {
                                        dispatch(setAngleEnabled({ enabled: e.target.checked }));
                                        commit({ angleEnabled: e.target.checked });
                                    }}
                                />
                                <Typography variant="body2">Minimum central angle</Typography>
                                <InfoTooltip text="How much of the shaft's circumference the line actually measured. A narrow arc gives the fit little to work with, so the centre estimate degrades sharply." />
                            </Stack>
                            <Typography variant="body2" sx={numberSx}>{rawSurface.angleLimit}&deg;</Typography>
                        </Stack>
                        <Slider
                            size="small"
                            min={0}
                            max={180}
                            step={1}
                            value={rawSurface.angleLimit}
                            disabled={isBusy || !rawSurface.angleEnabled}
                            onChange={(e, v) => dispatch(setAngleLimit({ angleLimit: v }))}
                            onChangeCommitted={(e, v) => commit({ angleLimit: v })}
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Switch
                                    size="small"
                                    checked={rawSurface.pointEnabled}
                                    disabled={isBusy}
                                    onChange={(e) => {
                                        dispatch(setPointEnabled({ enabled: e.target.checked }));
                                        commit({ pointEnabled: e.target.checked });
                                    }}
                                />
                                <Typography variant="body2">Minimum point count</Typography>
                                <InfoTooltip text="Valid returns in the line. Production drops a circle at or below this count." />
                            </Stack>
                            <Typography variant="body2" sx={numberSx}>{rawSurface.pointLimit}</Typography>
                        </Stack>
                        <Slider
                            size="small"
                            min={0}
                            max={Math.max(1000, rawSurface.lineWidth || 1600)}
                            step={10}
                            value={rawSurface.pointLimit}
                            disabled={isBusy || !rawSurface.pointEnabled}
                            onChange={(e, v) => dispatch(setPointLimit({ pointLimit: v }))}
                            onChangeCommitted={(e, v) => commit({ pointLimit: v })}
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Switch
                                    size="small"
                                    checked={rawSurface.sigmaEnabled}
                                    disabled={isBusy}
                                    onChange={(e) => {
                                        dispatch(setSigmaEnabled({ enabled: e.target.checked }));
                                        commit({ sigmaEnabled: e.target.checked });
                                    }}
                                />
                                <Typography variant="body2">Maximum sigma</Typography>
                                <InfoTooltip text="Diagnostic only - the AU has no residual setting, so this is off by default." />
                            </Stack>
                            <Typography variant="body2" sx={numberSx}>{rawSurface.maxSigma}&nbsp;µm</Typography>
                        </Stack>
                        <Slider
                            size="small"
                            min={0}
                            max={1000}
                            step={10}
                            value={rawSurface.maxSigma}
                            disabled={isBusy || !rawSurface.sigmaEnabled}
                            onChange={(e, v) => dispatch(setMaxSigma({ maxSigma: v }))}
                            onChangeCommitted={(e, v) => commit({ maxSigma: v })}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        disabled={isBusy || atProductionDefaults}
                        onClick={() => {
                            dispatch(setCircleFixEnabled({ enabled: true }));
                            dispatch(setAngleEnabled({ enabled: true }));
                            dispatch(setPointEnabled({ enabled: true }));
                            dispatch(setSigmaEnabled({ enabled: false }));
                            commit({
                                circleFixLimit: PRODUCTION_CIRCLE_FIX_LIMIT,
                                circleFixEnabled: true,
                                angleLimit: PRODUCTION_ANGLE_LIMIT,
                                angleEnabled: true,
                                pointLimit: PRODUCTION_POINT_LIMIT,
                                pointEnabled: true,
                                maxSigma: DEFAULT_MAX_SIGMA,
                                sigmaEnabled: false,
                            });
                        }}
                    >
                        Reset to production defaults
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography sx={cardTitleSx}>Shaft Ranges</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Drag across a chart above, or type start/stop directly below.
                    </Typography>

                    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                        {[
                            {
                                label: "Machine", shaft: "machine", range: rawSurface.machineRange,
                                startText: machineStartText, stopText: machineStopText,
                                setStartText: setMachineStartText, setStopText: setMachineStopText,
                                otherRange: rawSurface.motorRange,
                            },
                            {
                                label: "Motor", shaft: "motor", range: rawSurface.motorRange,
                                startText: motorStartText, stopText: motorStopText,
                                setStartText: setMotorStartText, setStopText: setMotorStopText,
                                otherRange: rawSurface.machineRange,
                            },
                        ].map(({ label, shaft, range, startText, stopText, setStartText, setStopText, otherRange }) => (
                            <Box key={shaft}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2">{label}</Typography>
                                    {range && (
                                        <IconButton
                                            size="small"
                                            onClick={() => dispatch(clearShaftRange({ shaft }))}
                                            aria-label={`Clear ${label} range`}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        label="Start (µm)" size="small" type="text" fullWidth
                                        inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                        value={startText}
                                        onChange={(e) => setStartText(e.target.value)}
                                        onBlur={() => commitShaftRange(shaft, startText, stopText, otherRange)}
                                    />
                                    <TextField
                                        label="Stop (µm)" size="small" type="text" fullWidth
                                        inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                        value={stopText}
                                        onChange={(e) => setStopText(e.target.value)}
                                        onBlur={() => commitShaftRange(shaft, startText, stopText, otherRange)}
                                    />
                                </Stack>
                            </Box>
                        ))}

                        {rangeError && (
                            <Alert severity="warning" sx={{ py: 0.25 }}>{rangeError}</Alert>
                        )}

                        {/* Only shown once both ranges are set - inferred from their relative
                            position, not an operator-entered flag. */}
                        {motorRight !== null && (
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                borderRadius: "8px",
                                padding: "8px 10px",
                                backgroundColor: tokens.surface2,
                            }}>
                                <Typography component="span" variant="body2">Motor position: </Typography>
                                <Typography component="span" variant="body2">
                                    {motorRight ? "Right" : "Left"}
                                </Typography>
                            </Box>
                        )}

                        <Divider />

                        <Typography variant="body2">Foot positions</Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                label="Distance to front foot (µm)" size="small" type="text" fullWidth
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                value={distanceToFFText}
                                onChange={(e) => setDistanceToFFText(e.target.value)}
                                onBlur={() => commitFootPosition(
                                    distanceToFFText, (v) => dispatch(setDistanceToFF({ distanceToFF: v }))
                                )}
                            />
                            <TextField
                                label="Front-to-rear foot (µm)" size="small" type="text" fullWidth
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                value={distanceFtoRText}
                                onChange={(e) => setDistanceFtoRText(e.target.value)}
                                onBlur={() => commitFootPosition(
                                    distanceFtoRText, (v) => dispatch(setDistanceFtoR({ distanceFtoR: v }))
                                )}
                            />
                        </Stack>
                        {footError && (
                            <Alert severity="warning" sx={{ py: 0.25 }}>{footError}</Alert>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {rangesAndFeetSet ? (
                <>
                    <RawSurfaceCorrectionCard commit={commitFilters} />
                    <NoiseReductionScatterCard commit={commitFilters} />
                    <FocusRemoveWorstCard commit={commitFilters} />
                </>
            ) : (
                <Card>
                    <CardContent>
                        <Typography sx={cardTitleSx}>Surface Correction &amp; Data Filters</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Set shaft ranges and foot positions above to enable.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* id, not a ref, so RawSurfaceSaveButton.js's "Calculate Shim Values" button - a
                sibling component in the rail, not a parent - can scroll to this card after its own
                dispatch resolves, without the two components needing to share a ref. */}
            <Card id="raw-surface-shim-values-card">
                <CardContent>
                    <Typography sx={cardTitleSx}>Shim Values</Typography>
                    {!rangesAndFeetSet && (
                        <Typography variant="caption" color="text.secondary">
                            Set shaft ranges and foot positions above to enable.
                        </Typography>
                    )}
                    {/* Last shim result from RawSurfaceSaveDialog or the live filter panels above,
                        cleared whenever the underlying fit changes (RawSurfaceSlice.js's
                        applyFit/setSelectedAuId) so it can never be mistaken for still matching
                        what's currently kept. */}
                    {rangesAndFeetSet && rawSurface.shims && (
                        <>
                            {/* Short enough to stay on one line at rail width - the previous wording
                                wrapped, which left the icon floating against the first of two lines.
                                alignItems keeps icon and text centred on each other regardless. */}
                            <Alert
                                severity={rawSurface.shimsSaved ? "success" : "warning"}
                                sx={{ mb: 1.5, mt: 1, alignItems: "center", py: 0.25 }}
                            >
                                {rawSurface.shimsSaved ? "Saved as a scan" : "Preview — not saved"}
                            </Alert>
                            {/* Which calibration produced these numbers. Small on purpose - it is
                                reference, not a headline - but present because the same capture
                                against a different calibration gives different shims, and until
                                now nothing on screen said which one was used. */}
                            {rawSurface.appliedCalibration && (
                                <Typography
                                    variant="caption"
                                    display="block"
                                    color={rawSurface.appliedCalibration.predatesCapture
                                        ? "warning.main" : "text.secondary"}
                                    sx={{ mb: 1 }}
                                >
                                    Calibration{" "}
                                    {new Date(rawSurface.appliedCalibration.dateTime).toLocaleString()}
                                    {rawSurface.appliedCalibration.scanCount
                                        ? ` · ${rawSurface.appliedCalibration.scanCount} scans`
                                        : ""}
                                    {rawSurface.appliedCalibration.predatesCapture
                                        ? " · taken after this capture"
                                        : ""}
                                </Typography>
                            )}
                            <Grid container direction="row" sx={{ marginLeft: "auto", marginRight: "auto" }}>
                                <Box className="shim-column shim-label" />
                                <Box className="shim-column shim-caption">Front</Box>
                                <Box className="shim-column shim-caption">Rear</Box>
                            </Grid>
                            <Grid container direction="row">
                                <Box className="shim-column shim-label">Vertical</Box>
                                <Box className="shim-column shim-value">{shimInches(rawSurface.shims.frontVertical)}</Box>
                                <Box className="shim-column shim-value">{shimInches(rawSurface.shims.rearVertical)}</Box>
                            </Grid>
                            <Grid container direction="row">
                                <Box className="shim-column shim-label">Horizontal</Box>
                                <Box className="shim-column shim-value">{shimInches(rawSurface.shims.frontHorizontal)}</Box>
                                <Box className="shim-column shim-value">{shimInches(rawSurface.shims.rearHorizontal)}</Box>
                            </Grid>

                            {rawSurface.summary && (
                                <>
                                    <Divider sx={{ mt: 1.5, mb: 1 }} />
                                    {renderStatsHeader()}
                                    {renderStatsRow(rawSurface.summary.machine, rawSurface.summary.motor, "y", "Vertical")}
                                    {renderStatsRow(rawSurface.summary.machine, rawSurface.summary.motor, "z", "Horizontal")}
                                </>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
}
